import axios from 'axios';
import i18n from '../i18n';

const runtime = (typeof window !== 'undefined' && window._env_) ? window._env_ : {};
export const API_URL = runtime.API_URL || process.env.REACT_APP_API_URL || 'http://localhost:8090';

export const TOKEN_KEY = 'SD_TOKEN';
export const USER_KEY = 'SD_USER';

const api = axios.create({ baseURL: `${API_URL}/api` });

// Proof that the admin re-entered their password for the job/quote open right now (see useMoneyLock).
// Kept in memory only and dropped when the page is left, so a reload or another page starts locked.
let moneyToken = null;
let onMoneyLocked = null;
export const setMoneyToken = (token) => { moneyToken = token; };
/** Called when the server says the amounts are locked (e.g. the unlock just expired). */
export const setMoneyLockedHandler = (fn) => { onMoneyLocked = fn; };

// Maintenance (a release on the server): the maintenance page answers 503 MAINTENANCE, and while the new backend
// starts the proxy answers 502/504. MaintenanceOverlay listens and waits for the server instead of error toasts.
let onMaintenance = null;
export const setMaintenanceHandler = (fn) => { onMaintenance = fn; };
export const isMaintenanceError = (error) => {
    const status = error?.response?.status;
    return (status === 503 && error.response.data?.code === 'MAINTENANCE') || status === 502 || status === 504;
};

api.interceptors.request.use((config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) config.headers.Authorization = `Bearer ${token}`;
    if (moneyToken) config.headers['X-Money-Unlock'] = moneyToken;
    return config;
});

api.interceptors.response.use(
    (res) => res,
    (error) => {
        const url = error.config?.url || '';
        // Expired or invalid session: clear it and send the user to login (but not for the login call itself).
        if (error.response?.status === 401 && !url.includes('/auth/login')) {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
            if (!window.location.pathname.startsWith('/login')) window.location.href = '/login?expired=1';
        }
        if (error.response?.status === 403 && error.response.data?.code === 'MONEY_LOCKED') onMoneyLocked?.();
        if (isMaintenanceError(error)) onMaintenance?.();
        return Promise.reject(error);
    }
);

/**
 * User-facing message for a failed request, in the active language. The server sends a
 * stable `code`; its English `message` is only used if there is no translation for it.
 */
export const errorMessage = (error, fallback) => {
    if (isMaintenanceError(error)) return i18n.t('maintenance.toast');
    const code = error?.response?.data?.code;
    if (code && i18n.exists(`errors.${code}`)) return i18n.t(`errors.${code}`);
    if (!error?.response) return i18n.t('errors.NETWORK');
    return fallback || i18n.t('common.error');
};

/** Server-side field validation errors, shown as a translated generic hint per field. */
export const fieldErrors = (error) => {
    const fe = error?.response?.data?.fieldErrors || {};
    return Object.fromEntries(Object.keys(fe).map(k => [k, i18n.t(k === 'email' ? 'validation.email' : 'validation.invalid')]));
};

export default api;
